/* OdmOsPlatformDetailsViewRenderer.js */

"use strict";
const _ = require("lodash");
const Forms = require("@gluenetworks/forms");
const Registrations = require("../../../../../RegistrationNamespaces");
const FormsComponent = Forms.ui.component;
const ChildViewComponent = require("../../../../widgets/childview/ChildViewRenderer");
const ManageImagesComponent = require("./subview/manage_images/ManageImagesComponent");
const ManageInstructionsComponent = require("./subview/manage_instructions/ManageInstructionsComponent");

const VIEW_ACTION_IDS = [
    "SavePlatformDetails",
    "CancelPlatformDetails",
    "ManageImages",
    "ManageInstructions",
    "AddImages",
    "AddInstructions",
];

// OS Family options for form dropdown
const OS_FAMILY_OPTIONS = [{Windows: "Windows"}, {Debian: "Debian"}];

const readOnlyFieldIds = [
    "linkCount",
    "instructionCount",
    "ignoreLinkIds",
    "deviceCount",
    "planCount",
    "updatedOn",
    "updatedBy",
    "createdOn",
    "createdBy",
];

class OdmOsPlatformDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("odm-os-platform-details-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;
        this._isDirty = false;

        this._formsComponent = new FormsComponent();

        // Bind all handlers
        this._onPlatformDetailsSave = this._onPlatformDetailsSave.bind(this);
        this._onPlatformDetailsCancel = this._onPlatformDetailsCancel.bind(this);
        this._onManageImages = this._onManageImages.bind(this);
        this._onManageInstructions = this._onManageInstructions.bind(this);
        this._onAddImages = this._onAddImages.bind(this);
        this._onAddInstructions = this._onAddInstructions.bind(this);
        this._onDirty = this._onDirty.bind(this);
        this._debouncedOnPlatformPropertiesChanged = _.debounce(
            this._onPlatformPropertiesChanged.bind(this),
            1000,
            false
        );
        this._onFormsPendingPatchesChanged = _.debounce(
            this._onFormsPendingPatchesChanged.bind(this),
            500,
            false
        );

        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SavePlatformDetails",
            handler: this._onPlatformDetailsSave,
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelPlatformDetails",
            handler: this._onPlatformDetailsCancel,
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Manage Images",
            name: "ManageImages",
            handler: this._onManageImages,
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Manage Instructions",
            name: "ManageInstructions",
            handler: this._onManageInstructions,
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Add Images",
            name: "AddImages",
            handler: this._onAddImages,
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Add Instructions",
            name: "AddInstructions",
            handler: this._onAddInstructions,
        });

        this._renderingForm = false;
        this._updateViewActions();
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    async _onManageImages() {
        this._manageImagesComponent = new ManageImagesComponent(this._model);
        this._model.childViewOpen = true;
        this._model.childViewName = "manage_images";
        await this._manageImagesComponent.renderer.render();

        this._childViewRenderer = new ChildViewComponent({
            data: this._manageImagesComponent.renderer.rootElement,
            backLink: "Details",
            title: "Manage Images",
            childData: this._manageImagesComponent,
            parentData: this._model,
            backButton: true,
            // backCallback: this._editView._renderer._onSaveRuleClick,
        });
    }

    async _onManageInstructions() {
        this._manageInstructionsComponent = new ManageInstructionsComponent(this._model);
        this._model.childViewOpen = true;
        this._model.childViewName = "manage_instructions";
        await this._manageInstructionsComponent.renderer.render();

        this._childViewRenderer = new ChildViewComponent({
            data: this._manageInstructionsComponent.renderer.rootElement,
            backLink: "Details",
            title: "Manage Instructions",
            childData: this._manageInstructionsComponent,
            parentData: this._model,
            backButton: true,
        });
    }

    _onAddImages() {
        this._model._gameEditor.runAction(Registrations.ODM_OS_PLATFORM_EDITOR_ACTIONS_ADD_IMAGES);
    }

    _onAddInstructions() {
        this._model._gameEditor.runAction(Registrations.PLATFORM_EXPLORER_ACTION_ADD_INSTRUCTIONS);
    }

    _registerListeners() {
        this._model.on("Dirty", this._onDirty);
        this._model.on("PlatformPropertiesChanged", this._debouncedOnPlatformPropertiesChanged);
        this._formsComponent.on("PendingPatchesChanged", this._onFormsPendingPatchesChanged);
    }

    _unregisterListeners() {
        this._model.removeListener("Dirty", this._onDirty);
        this._model.removeListener(
            "PlatformPropertiesChanged",
            this._debouncedOnPlatformPropertiesChanged
        );
        this._formsComponent.removeListener(
            "PendingPatchesChanged",
            this._onFormsPendingPatchesChanged
        );
    }

    _onDirty(isDirty) {
        this._isDirty = isDirty;
        this._updateViewActions();
    }

    _onPlatformPropertiesChanged(pristineProperties, before) {
        if (!this._renderingForm) {
            let formVal = this._formsComponent.getValue(); // Data from Jtype form
            let odmOsPlatformPropsVal = this._getPristineValue(); // Data from _pristineProperties i.e. sync'd with BE

            let updateReadOnlyFields = () => {
                _.keys(pristineProperties).map((id) => {
                    if (
                        !_.isUndefined(odmOsPlatformPropsVal[id]) &&
                        readOnlyFieldIds.indexOf(id) === -1
                    ) {
                        odmOsPlatformPropsVal[id] = formVal[id];
                    } else {
                        if (!_.isEqual(pristineProperties[id], this._model._odmOsPlatform[id])) {
                            this._model._odmOsPlatform.setProperty(id, pristineProperties[id]);
                        }
                    }
                });

                this._formsComponent.setPristineValue(odmOsPlatformPropsVal);
                return Promise.resolve();
            };

            // Usual isDirty is limited to its pendingPatches non-empty aspect here
            // because the only relationship is parentSite, has no impact on form
            // and can arrive late if connection slow causing veil to come up
            if (this._model._odmOsPlatform.pendingPatches) {
                let isReadOnlyUpdate = true;
                _.keys(pristineProperties).map((id) => {
                    // No need to run these checks if isReadOnlyUpdate is already false
                    if (!isReadOnlyUpdate) {
                        return;
                    }

                    if (
                        readOnlyFieldIds.indexOf(id) === -1 &&
                        !_.isEqual(_.get(pristineProperties, id, null), _.get(before, id, null))
                    ) {
                        isReadOnlyUpdate = false;
                    }
                });

                if (!isReadOnlyUpdate) {
                    this._model.gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message:
                                "This ODM OS Platform has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                            title: "Update Confirmation Check",
                            confirmDetails: {
                                text: "Yes",
                                type: "text",
                            },
                        },
                        (this._onPlatformDetailsCancelAction =
                            this._onPlatformDetailsCancelAction.bind(this)),
                        updateReadOnlyFields
                    );
                } else {
                    updateReadOnlyFields();
                }
            } else {
                this._formsComponent.setPristineValue(odmOsPlatformPropsVal);
            }
        }
    }

    _onFormsPendingPatchesChanged() {
        if (!this._renderingForm) {
            let form = this._formsComponent.getValue();

            // Define the form properties mapping for ODM OS Platform
            const formProperties = {
                name: "name",
                manufacturer: "manufacturer",
                deviceType: "deviceType",
                model: "model",
                osName: "osName",
                osFamily: "osFamily",
                osVersion: "osVersion",
                osReleaseName: "osReleaseName",
                machineArchitecture: "machineArchitecture",
                language: "language",
                locale: "locale",
                patchDepth: "patchDepth",
            };

            const setProperty = (name) => {
                if (!name) {
                    return;
                }
                let formPropertyValue = _.get(form, formProperties[name]);
                let pristinePropertyValue = _.get(
                    this.model._odmOsPlatform.getPristineProperties(),
                    name
                );

                let methodName = "set" + _.upperFirst(name);

                if (this.isEmpty(formPropertyValue) && this.isEmpty(pristinePropertyValue)) {
                    if (_.get(this._model._odmOsPlatform, name) !== pristinePropertyValue) {
                        _.invoke(this._model._odmOsPlatform, methodName, pristinePropertyValue);
                    }
                } else {
                    if (formPropertyValue !== pristinePropertyValue) {
                        _.invoke(this._model._odmOsPlatform, methodName, formPropertyValue);
                    } else if (_.get(this._model._odmOsPlatform, name) !== pristinePropertyValue) {
                        _.invoke(this._model._odmOsPlatform, methodName, pristinePropertyValue);
                    }
                }
            };

            const setProperties = (names = []) => names.forEach(setProperty);

            const propertiesToSet = [
                "name",
                "manufacturer",
                "deviceType",
                "model",
                "osName",
                "osFamily",
                "osVersion",
                "osReleaseName",
                "machineArchitecture",
                "language",
                "locale",
                "patchDepth",
            ];

            setProperties(propertiesToSet);
        }
    }

    isEmpty(value) {
        return value === null || value === undefined || value === "";
    }

    _renderForm() {
        return Promise.resolve()
            .then(() => {
                const platform = this._model._odmOsPlatform;
                const jtype = this._buildFormJtype(platform);
                const pristineValue = this._getPristineValue();

                this._formsComponent.setTypeInformation(jtype, true);
                this._formsComponent.setPristineValue(pristineValue, true);

                this._renderingForm = true;
                return this._formsComponent.renderer.render().then(() => {
                    // Delay so rendering can complete
                    new Promise((resolve) => {
                        setTimeout(resolve, 500);
                    }).then(() => {
                        this._renderingForm = false;
                    });
                });
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "OdmOsPlatformDetailsViewRenderer._renderForm() error."
                );
            });
    }

    _getPristineValue() {
        if (!this._model._odmOsPlatform) {
            return {};
        }

        const platform = this._model._odmOsPlatform;
        const pristineProperties = platform.getPristineProperties();

        // Return pristine values for form comparison
        return {
            name: pristineProperties.name || "",
            manufacturer: pristineProperties?.manufacturer || "",
            deviceType: pristineProperties?.deviceType || "",
            model: pristineProperties?.model || "",
            osName: pristineProperties?.osName || "",
            osFamily: pristineProperties?.osFamily || "",
            osVersion: pristineProperties?.osVersion || "",
            osReleaseName: pristineProperties?.osReleaseName || "",
            machineArchitecture: pristineProperties?.machineArchitecture || "",
            language: pristineProperties?.language || "",
            locale: pristineProperties?.locale || "",
            patchDepth: pristineProperties?.patchDepth,
            linkCount: pristineProperties?.linkCount || 0,
            instructionCount: pristineProperties?.instructionCount || 0,
            deviceCount: pristineProperties?.deviceCount || 0,
            planCount: pristineProperties?.planCount || 0,
            updatedOn: this._toLocaleString(platform.updatedOn),
            updatedBy: platform.updatedBy || "",
            createdOn: this._toLocaleString(platform.createdOn),
            createdBy: platform.createdBy || "",
        };
    }

    _buildFormJtype(platform) {
        const properties = platform.getProperties();
        let jtype = {};

        // Editable fields
        jtype.name = properties.name || "";
        jtype["name@Display.label"] = "Name";
        jtype["name@Display.description"] = "Editable unique name of the IoT Platform";
        jtype["name@Required"] = true;

        jtype.manufacturer = properties.manufacturer || "";
        jtype["manufacturer@Display.label"] = "Manufacturer";
        jtype["manufacturer@Display.description"] =
            'Required manufacturer name, e.g., "Physio-Control"';
        jtype["manufacturer@Required"] = true;
        jtype["manufacturer@Enum"] = (this._model._manufacturerOptions || []).map((obj) => {
            const key = Object.keys(obj)[0];
            return key;
        });
        jtype["manufacturer@Display.enum"] = (this._model._manufacturerOptions || []).map(
            (obj) => Object.values(obj)[0]
        );

        jtype.deviceType = properties.deviceType || "";
        jtype["deviceType@Display.label"] = "IoT Device Type";
        jtype["deviceType@Display.description"] = 'Required device type, e.g., "Defibrillator"';
        jtype["deviceType@Required"] = true;
        jtype["deviceType@Enum"] = (this._model._deviceTypeOptions || []).map((obj) => {
            const key = Object.keys(obj)[0];
            return key;
        });
        jtype["deviceType@Display.enum"] = (this._model._deviceTypeOptions || []).map(
            (obj) => Object.values(obj)[0]
        );

        jtype.model = properties.model || "";
        jtype["model@Display.label"] = "Model";
        jtype["model@Display.description"] = 'Required device model, e.g., "LIFEPAK 20e"';
        jtype["model@Required"] = true;
        jtype["model@Enum"] = (this._model._modelOptions || []).map((obj) => {
            const key = Object.keys(obj)[0];
            return key;
        });
        jtype["model@Display.enum"] = (this._model._modelOptions || []).map(
            (obj) => Object.values(obj)[0]
        );

        jtype.osName = properties.osName || "";
        jtype["osName@Display.label"] = "OS Name";
        jtype["osName@Display.description"] =
            'OS name (required with OS Version), e.g., "Debian GNU/Linux 12 (bookworm)"';

        jtype.osFamily = properties.osFamily || "";
        jtype["osFamily@Display.label"] = "OS Family";
        jtype["osFamily@Display.description"] = "Operating system family";
        jtype["osFamily@Enum"] = OS_FAMILY_OPTIONS.map((obj) => {
            const key = Object.keys(obj)[0];
            return key;
        });
        jtype["osFamily@Display.enum"] = OS_FAMILY_OPTIONS.map((obj) => Object.values(obj)[0]);

        jtype.osReleaseName = properties.osReleaseName || "";
        jtype["osReleaseName@Display.label"] = "OS Release Name";
        jtype["osReleaseName@Display.description"] =
            'OS release name (alternative to OS Name + Version), e.g., "bookworm"';

        jtype.osVersion = properties.osVersion || "";
        jtype["osVersion@Display.label"] = "OS Version";
        jtype["osVersion@Display.description"] = 'OS version (required with OS Name), e.g., "12.4"';

        jtype.machineArchitecture = properties.machineArchitecture || "";
        jtype["machineArchitecture@Display.label"] = "Machine Architecture";
        jtype["machineArchitecture@Display.description"] =
            'Optional free-form architecture, e.g., "amd64"';

        jtype.language = properties.language || "";
        jtype["language@Display.label"] = "OS Language";
        jtype["language@Display.description"] = 'Optional free-form language, e.g., "en_US.UTF-8"';

        jtype.locale = properties.locale || "";
        jtype["locale@Display.label"] = "Locale";
        jtype["locale@Display.description"] = "Optional free-form locale setting";

        // Line break
        jtype.lineBreak1 = null;
        jtype["lineBreak1@Display.label"] = "&nbsp;";

        jtype.patchDepth = properties.patchDepth;
        jtype["patchDepth@Display.label"] = "Patch Depth";
        jtype["patchDepth@Display.description"] =
            "Number of update patches to retrieve for the platform";
        jtype["patchDepth@Number"] = {min: 1, max: 12, onlyInteger: true};

        // Line break (separates Patch Depth from the Image counters)
        jtype.lineBreak2 = null;
        jtype["lineBreak2@Display.label"] = "&nbsp;";

        // Read-only fields
        jtype.linkCount = properties.linkCount || 0;
        jtype["linkCount@Display.label"] = "Images";
        jtype["linkCount@Display.description"] = "Number of images linked to the platform";
        jtype["linkCount@Display.readOnly"] = true;

        jtype.instructionCount = properties.instructionCount || 0;
        jtype["instructionCount@Display.label"] = "Instructions";
        jtype["instructionCount@Display.description"] =
            "Number of instructions linked to the platform";
        jtype["instructionCount@Display.readOnly"] = true;

        jtype.deviceCount = properties.deviceCount || 0;
        jtype["deviceCount@Display.label"] = "Devices";
        jtype["deviceCount@Display.description"] = "Number of IoT Devices linked to the platform";
        jtype["deviceCount@Display.readOnly"] = true;

        jtype.planCount = properties.planCount || 0;
        jtype["planCount@Display.label"] = "OS Plans";
        jtype["planCount@Display.description"] = "Number of IoT OS Plans linked to the platform";
        jtype["planCount@Display.readOnly"] = true;

        // Line break
        jtype.lineBreak3 = null;
        jtype["lineBreak3@Display.label"] = "&nbsp;";

        jtype.updatedBy = properties.updatedBy || "";
        jtype["updatedBy@Display.label"] = "Updated By";
        jtype["updatedBy@Display.description"] =
            "Username of the person who last updated the platform";
        jtype["updatedBy@Display.readOnly"] = true;

        jtype.updatedOn = properties.updatedOn || "";
        jtype["updatedOn@Display.label"] = "Updated On";
        jtype["updatedOn@Display.description"] =
            "Timestamp indicating the platform's most recent update";
        jtype["updatedOn@Display.readOnly"] = true;

        jtype.createdBy = properties.createdBy || "";
        jtype["createdBy@Display.label"] = "Created By";
        jtype["createdBy@Display.description"] = "Username of the person who created the platform";
        jtype["createdBy@Display.readOnly"] = true;

        jtype.createdOn = properties.createdOn || "";
        jtype["createdOn@Display.label"] = "Created On";
        jtype["createdOn@Display.description"] =
            "Timestamp indicating the platform's creation date";
        jtype["createdOn@Display.readOnly"] = true;

        return jtype;
    }

    _onPlatformDetailsSave() {
        return this._model
            .savePlatform()
            .then(() => {
                this._model.gameEditor.clearOnBeforeUnload();
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "OdmOsPlatformDetailsViewRenderer._onPlatformDetailsSave() error"
                );
            });
    }

    _onPlatformDetailsCancel() {
        return this._model
            .resetPlatformProperties()
            .then(() => {
                this._model.gameEditor.clearOnBeforeUnload();
                this._renderForm();
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "OdmOsPlatformDetailsViewRenderer._onPlatformDetailsCancel() error"
                );
            });
    }

    async _onPlatformDetailsCancelAction() {
        await this._model.resetPlatformProperties();
        this._renderForm();
    }

    _toLocaleString(val) {
        let d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    render(withLoading = false) {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._formsComponent.renderer.rootElement);
        }
        const promises = [this._model.whenModelIsReady()];
        if (withLoading) {
            promises.push(this._model._modalUserFeedback.displayLoading());
        }

        return Promise.race(promises)
            .then(() => {
                return this._renderForm();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "OdmOsPlatformDetailsViewRenderer.render() error");
            });
    }

    shutdown() {
        // Cancel any pending debounced operations
        this._debouncedOnPlatformPropertiesChanged.cancel();
        this._onFormsPendingPatchesChanged.cancel();

        this._unregisterListeners();

        const promArr = [this._formsComponent.shutdown()];

        return Promise.all(promArr).then(
            () => {
                // Clean up components
                this._formsComponent = undefined;
                this._manageImagesComponent = undefined;
                this._childViewRenderer = undefined;

                // Clean up state
                this._isDirty = undefined;
                this._renderingForm = undefined;

                // Clean up references
                this._model = undefined;
                this._logger = undefined;

                // Clean up DOM last
                if (this._rootElem) {
                    this._rootElem.remove();
                    this._rootElem = undefined;
                }
            },
            (err) => {
                if (this._logger) {
                    this._logger.warn(
                        {error: err},
                        "OdmOsPlatformDetailsViewRenderer.shutdown() error"
                    );
                }
            }
        );
    }

    _setAllViewActionsVisible(isVisible) {
        VIEW_ACTION_IDS.forEach((action) => this._setViewActionVisible(action, isVisible));
    }

    _setViewActionVisible(actionName, isVisible) {
        const {gameEditor} = this.model;
        gameEditor[isVisible ? "showViewAction" : "hideViewAction"](actionName);
    }

    _updateViewActions() {
        const childViewOpen = this._model.childViewOpen || false;
        const childViewName = this._model.childViewName || "";
        const showSaveCancel = this._isDirty || false;

        this._setViewActionVisible("SavePlatformDetails", showSaveCancel && !childViewOpen);
        this._setViewActionVisible("CancelPlatformDetails", showSaveCancel && !childViewOpen);
        this._setViewActionVisible("AddImages", childViewOpen && childViewName === "manage_images");
        this._setViewActionVisible(
            "AddInstructions",
            childViewOpen && childViewName === "manage_instructions"
        );
        this._setViewActionVisible("ManageImages", !childViewOpen);
        this._setViewActionVisible("ManageInstructions", !childViewOpen);
    }

    _showViewActions() {
        this._updateViewActions();
    }

    _hideViewActions() {
        this._updateViewActions();
    }

    _hideLeftFooterContainer() {}

    _refreshGrid() {}

    _showLeftFooterContainer() {}

    /*******************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS   */
    /*******************************************/
}

module.exports = OdmOsPlatformDetailsViewRenderer;
